import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const Search = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Search</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Perform hybrid semantic search on documents within a namespace. Uses vector similarity search combined
        with full-text search, and optionally enhances results with knowledge graph traversal to find related entities.
        Results are reranked for relevance before being returned.
      </p>

      <EndpointCard
        method="POST"
        path="/namespaces/{namespace_uuid}/search"
        title="Search Documents"
        description="Performs hybrid semantic search on documents within a namespace. Results combine vector similarity, full-text search, and optional knowledge graph enhancement."
        tags={['Authentication: Required', 'Hybrid Search']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          },
          query: {
            use_graph: { required: false, description: 'Enable graph-enhanced search (default: true)', default: true }
          }
        }}
        requestBody={{
          query: "customer support procedures",
          limit: 5
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>namespace_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the namespace</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>use_graph</code></td>
                  <td>boolean</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>true</td>
                  <td>Enable graph-enhanced search using knowledge graph (requires Neo4j)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "query": "customer support procedures",
  "limit": 5
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>query</strong> (required): The search query text<br/>
              <strong>limit</strong> (optional, default: 5): Maximum number of results to return
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/namespaces/{namespace_uuid}/search?use_graph=true" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "customer support procedures",
    "limit": 5
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "uuid": "{chunk_uuid}",
    "document_uuid": "{document_uuid}",
    "content": "When handling customer support escalations, first identify the escalation trigger...",
    "chunk_index": 0,
    "metadata": {
      "start_char": 0,
      "end_char": 500
    }
  }
]`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Search process:</strong> The endpoint performs hybrid search (vector + full-text), optionally
              expands queries using the knowledge graph to find related entities, then reranks results for optimal relevance.
            </p>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default Search;
